import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { insertDocument } from '../api/mongoConsole'

interface Props {
  database: string
  collection: string
  onClose: () => void
}

const TEMPLATE = '{\n  \n}'

export function InsertDocumentModal({ database, collection, onClose }: Props) {
  const queryClient = useQueryClient()
  const [docJson, setDocJson] = useState(TEMPLATE)
  const [jsonError, setJsonError] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => insertDocument(database, collection, {
      document: JSON.parse(docJson),
      reason,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mongo-query'] })
      onClose()
    },
  })

  const handleJsonChange = (val: string) => {
    setDocJson(val)
    try { JSON.parse(val); setJsonError(null) } catch { setJsonError('Invalid JSON') }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <div>
            <h3 className="font-semibold text-gray-900">Insert Document</h3>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{database}.{collection}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 min-h-0">
          <textarea
            value={docJson}
            onChange={e => handleJsonChange(e.target.value)}
            className={`w-full min-h-[300px] h-full font-mono text-xs bg-gray-900 text-green-300 rounded p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary ${jsonError ? 'ring-2 ring-red-500' : ''}`}
          />
        </div>

        <div className="border-t p-4 shrink-0 space-y-3">
          {jsonError && <p className="text-xs text-red-600">{jsonError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <input
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why is this document being inserted?"
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          {mutation.isError && (
            <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => mutation.mutate()}
              disabled={!!jsonError || !reason.trim() || mutation.isPending}>
              <Plus className="h-4 w-4 mr-1.5" />
              {mutation.isPending ? 'Inserting…' : 'Insert Document'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
